import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { TimeAgo } from "components/TimeAgo/TimeAgo";

interface DashboardWidgetLiveBarProps {
    live: boolean;
    lastUpdatedAt: string | null;
    onRefresh: () => void;
    isRefreshing: boolean;
    /** Show manual refresh while `live` is true (e.g. polling paused after execution ended). */
    showManualRefresh?: boolean;
    refreshLabel?: string;
    /**
     * Last re-fetch error while stale content is being kept on screen. Shown
     * inline next to the "Updated …" label (only once we have data, i.e.
     * `lastUpdatedAt` is set); cleared automatically on the next success.
     */
    error?: string | null;
}

/**
 * Footer shown on widgets that support live refresh — timestamp plus a manual
 * refresh control. The refresh button is shown on EVERY such widget regardless
 * of whether the widget auto-refreshes (`live`): on a live widget it simply
 * forces an immediate re-fetch (always giving "Updated just now" feedback), so
 * the button's presence no longer silently signals "this widget does not
 * auto-update". The button sits directly next to the "Updated …" label.
 * (`live` / `showManualRefresh` are retained on the props for call-site
 * compatibility but no longer gate visibility.)
 */
export const DashboardWidgetLiveBar = ({
    lastUpdatedAt,
    onRefresh,
    isRefreshing,
    refreshLabel = "Refresh",
    error = null,
}: DashboardWidgetLiveBarProps) => (
    <footer className="DashboardWidgetLiveBar">
        <span className="DashboardWidgetLiveBar__status">
            {lastUpdatedAt && (
                <span className="DashboardWidgetLiveBar__updated">
                    Updated <TimeAgo datetime={lastUpdatedAt} hideTooltip />
                </span>
            )}
            <Button
                className="DashboardWidgetLiveBar__refresh"
                variation="icon"
                icon={IconCollection.refresh}
                ariaLabel={refreshLabel}
                title={refreshLabel}
                labelOnlyAria
                loading={isRefreshing}
                disabled={isRefreshing}
                onClick={onRefresh}
            />
            {error && lastUpdatedAt && (
                <span className="DashboardWidgetLiveBar__error" title={error}>
                    {" · "}
                    {error}
                </span>
            )}
        </span>
    </footer>
);
